import { useNavigate, useParams } from "react-router-dom";
import { useInsightsCtx } from "./InsightsLayout";
import { useBuildingReport } from "./hooks/useBuildingReport";
import {
  SECTION_ACTIVITY,
  SECTION_ALERTS,
  SECTION_EQUIPMENT,
  changeText,
  hoursText,
  type BuildingReport,
  type UnitReport,
} from "./buildingReport";
import Spinner from "../../components/ui/Spinner";
import ErrorState from "../../components/ui/ErrorState";

/**
 * One property's weekly report, on screen.
 *
 * The same document the scheduled job emails, rendered for the dashboard. A
 * site manager reads the email, clicks through, and has to find the number they
 * just read, so both come from the same model and the same counting rules.
 *
 * Deliberately says nothing about presence. That is the wellness question,
 * asked of a different reader by a different feature (REQ-BLDG-3), and this
 * page must not appear to answer it.
 */

const TONES: Record<string, string> = {
  shutoffs: "text-alert-critical",
  alerts: "text-alert-warning",
  offline: "text-status-offline",
};

function Kpi({ value, label }: { value: number; label: string }) {
  return (
    <div className="px-5 py-3">
      <div className="text-3xl font-bold text-ink-primary dark:text-white">
        {value}
      </div>
      <div className="text-xs text-ink-tertiary">{label}</div>
    </div>
  );
}

function plural(count: number, singular: string, other?: string) {
  return count === 1 ? singular : other || `${singular}s`;
}

/** A unit's downtime, or an honest "unknown". */
function offlineCell(unit: UnitReport): string {
  if (unit.offlineHours === null) return "unknown";
  if (unit.offlineHours === 0) return "none";
  return hoursText(unit.offlineHours);
}

function Comparison({ report }: { report: BuildingReport }) {
  if (!report.previous || !report.delta) return null;

  const rows: [string, number, number, number][] = [];
  if (report.sections.includes(SECTION_ACTIVITY)) {
    rows.push([
      "Cooking sessions",
      report.totals.sessions,
      report.previous.sessions,
      report.delta.sessions,
    ]);
  }
  if (report.sections.includes(SECTION_ALERTS)) {
    rows.push(
      ["Alerts", report.totals.alerts, report.previous.alerts, report.delta.alerts],
      [
        "Safety shut-offs",
        report.totals.shutoffs,
        report.previous.shutoffs,
        report.delta.shutoffs,
      ]
    );
  }
  if (rows.length === 0) return null;

  return (
    <section className="mb-8">
      {/*
        Never labelled "trend". That word belongs to the 28 day graded badge
        elsewhere in the dashboard, and two different measures wearing one name
        is how a reader stops trusting both. This block is labelled identically
        to the email so a reader clicking through finds what they just read.
      */}
      <h2 className="mb-2 text-base font-semibold text-ink-primary dark:text-white">
        Compared with the previous 7 days
      </h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-ink-tertiary/40 text-left">
            <th className="py-2 pr-3 font-semibold"> </th>
            <th className="py-2 pr-3 text-right font-semibold">This period</th>
            <th className="py-2 pr-3 text-right font-semibold">Previous</th>
            <th className="py-2 text-right font-semibold">Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, now, before, delta]) => (
            <tr key={label} className="border-b border-ink-tertiary/20">
              <td className="py-2 pr-3">{label}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{now}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-ink-tertiary">
                {before}
              </td>
              <td className="py-2 text-right tabular-nums">{changeText(delta)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function BuildingReportPage() {
  const { buildingId = "" } = useParams();
  const navigate = useNavigate();
  const { buildings, buildingsLoading } = useInsightsCtx();

  const building = buildings.find(
    (b: { buildingId: string }) => b.buildingId === buildingId
  );

  const { report, status, unscopedSensors } = useBuildingReport(
    buildingId,
    building?.name ?? "",
    undefined,
    // Which properties this organization owns, so a sensor whose current tenure
    // belongs to a previous owner is counted as unscoped rather than silently
    // contributing their figures to these totals.
    buildings.map((b: { name: string }) => b.name).filter(Boolean)
  );

  if (buildingsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (!building) {
    return (
      <ErrorState
        title="Property not found"
        message="We couldn't find a property matching this URL."
        onRetry={() => navigate("/insights")}
      />
    );
  }

  if (status === "error") {
    return (
      <ErrorState
        title="Report unavailable"
        message="We couldn't load this property's report. Please try again."
        onRetry={() => navigate(0)}
      />
    );
  }

  if (status === "loading" || !report) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  const { totals, sections } = report;
  const showActivity = sections.includes(SECTION_ACTIVITY);
  const showAlerts = sections.includes(SECTION_ALERTS);
  const showEquipment = sections.includes(SECTION_EQUIPMENT);

  return (
    <div className="print-root mx-auto max-w-5xl px-4 py-6">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(`/insights/properties/${buildingId}`)}
          className="text-sm text-ink-tertiary hover:text-ink-primary dark:hover:text-white"
        >
          Back to sensors
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded bg-primary px-4 py-2 text-sm font-semibold text-black"
        >
          Print or save PDF
        </button>
      </div>

      <header className="mb-6">
        <h1 className="text-xl font-bold text-ink-primary dark:text-white">
          {report.buildingName}
        </h1>
        <p className="text-sm text-ink-tertiary">
          Weekly property report, {report.periodLabel}. {totals.sensors}{" "}
          {plural(totals.sensors, "unit")}.
        </p>
      </header>

      {/* Which sensors' figures are not scoped to this property. Printed, not
          omitted, for the same reason as the gap notice below: totals that may
          include another address's alerts must say so, or the reader will assume
          they are clean. */}
      {/* Deliberately does NOT say "no installation record": a sensor lands on
          this list either because it has no record at all, or because the most
          recent one is at a property we cannot match to this organization. Both
          mean the same thing for these totals, and naming only the first reason
          would be wrong about the second. */}
      {unscopedSensors.length > 0 && (
        <p className="mb-4 text-sm italic text-ink-tertiary">
          {unscopedSensors.length === totals.sensors
            ? "We can't confirm where any of this property's sensors were installed, so these figures cover wherever each sensor has been rather than only this property."
            : `We can't confirm where ${unscopedSensors.length} of ${
                totals.sensors
              } ${plural(totals.sensors, "sensor")} here ${
                unscopedSensors.length === 1 ? "was" : "were"
              } installed, so ${
                unscopedSensors.length === 1 ? "its figures cover" : "their figures cover"
              } wherever ${
                unscopedSensors.length === 1 ? "it has" : "they have"
              } been rather than only this property.`}
        </p>
      )}

      {/*
        Printed, never omitted. Under-reporting on a safety product without
        saying so is worse than admitting the gap: a manager who knows a unit
        was unmonitored can go and look at it.
      */}
      {showEquipment && totals.offlineHoursUnknown > 0 && (
        <p className="mb-4 text-sm italic text-alert-warning">
          {totals.offlineHoursUnknown}{" "}
          {plural(totals.offlineHoursUnknown, "unit")} had no connection record
          for this period, so{" "}
          {totals.offlineHoursUnknown === 1 ? "its" : "their"} offline time is
          shown as unknown rather than as none.
        </p>
      )}

      <div className="mb-8 flex flex-wrap divide-x divide-ink-tertiary/20 rounded border border-ink-tertiary/20">
        {showAlerts && (
          <>
            <Kpi
              value={totals.shutoffs}
              label={plural(totals.shutoffs, "safety shut-off")}
            />
            <Kpi value={totals.alerts} label={plural(totals.alerts, "alert")} />
          </>
        )}
        {showActivity && (
          <Kpi
            value={totals.sessions}
            label={plural(totals.sessions, "cooking session")}
          />
        )}
        {showEquipment && (
          <Kpi
            value={totals.offline}
            label={`${plural(totals.offline, "unit")} offline now`}
          />
        )}
      </div>

      <section className="mb-8">
        <h2 className="mb-2 text-base font-semibold text-ink-primary dark:text-white">
          Needs attention
        </h2>
        {report.attention.length > 0 ? (
          <ul className="space-y-2">
            {report.attention.map((item) => (
              <li key={item.scsName} className="text-sm">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/insights/properties/${buildingId}/sensors/${encodeURIComponent(
                        item.scsName
                      )}`
                    )
                  }
                  className="font-semibold text-ink-primary underline-offset-2 hover:underline dark:text-white"
                >
                  {item.name}
                </button>
                <span className="text-ink-tertiary">: </span>
                {item.issues.map((issue, i) => (
                  <span key={issue.kind}>
                    {i > 0 && <span className="text-ink-tertiary">, </span>}
                    <span className={TONES[issue.kind] || ""}>{issue.text}</span>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        ) : (
          // Printed, not omitted. On a safety product the all-clear is the
          // point of the report, not the absence of content.
          <p className="text-sm text-ink-tertiary">
            No units needed attention this period.
          </p>
        )}
      </section>

      <Comparison report={report} />

      <section>
        <h2 className="mb-2 text-base font-semibold text-ink-primary dark:text-white">
          All units
        </h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-ink-tertiary/40 text-left">
              <th className="py-2 pr-3 font-semibold">Unit</th>
              {showActivity && (
                <th className="py-2 pr-3 text-right font-semibold">Sessions</th>
              )}
              {showAlerts && (
                <>
                  <th className="py-2 pr-3 text-right font-semibold">Alerts</th>
                  <th className="py-2 pr-3 text-right font-semibold">Shut-offs</th>
                </>
              )}
              {showEquipment && (
                <th className="py-2 text-right font-semibold">Offline</th>
              )}
            </tr>
          </thead>
          <tbody>
            {report.units.map((unit) => (
              <tr key={unit.scsName} className="border-b border-ink-tertiary/20">
                <td className="py-2 pr-3">{unit.name}</td>
                {showActivity && (
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {unit.sessions}
                  </td>
                )}
                {showAlerts && (
                  <>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {unit.alerts}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {unit.shutoffs}
                    </td>
                  </>
                )}
                {showEquipment && (
                  <td className="py-2 text-right tabular-nums">
                    {offlineCell(unit)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
